// AES-GCM helpers for encrypting sensitive offline rows.
// Key is derived per-session from the Supabase JWT + a persistent device salt
// stored in localStorage. The raw key is never persisted.

const SALT_KEY = "makai.device_salt.v1";
let _key: CryptoKey | null = null;
let _keyFingerprint: string | null = null;

function getDeviceSalt(): Uint8Array {
  if (typeof window === "undefined") return new Uint8Array(16);
  let b64 = localStorage.getItem(SALT_KEY);
  if (!b64) {
    const buf = new Uint8Array(16);
    crypto.getRandomValues(buf);
    b64 = btoa(String.fromCharCode(...buf));
    localStorage.setItem(SALT_KEY, b64);
  }
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

/** Derive (and cache) the AES-GCM key from a session secret (e.g. JWT). */
export async function ensureKey(sessionSecret: string | null | undefined): Promise<CryptoKey | null> {
  if (typeof crypto === "undefined" || !crypto.subtle) return null;
  if (!sessionSecret) {
    _key = null;
    _keyFingerprint = null;
    return null;
  }
  if (_key && _keyFingerprint === sessionSecret.slice(-24)) return _key;

  const salt = getDeviceSalt();
  const baseKey = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(sessionSecret) as unknown as BufferSource,
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  _key = await crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: salt as unknown as BufferSource, iterations: 100_000, hash: "SHA-256" },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
  _keyFingerprint = sessionSecret.slice(-24);
  return _key;
}

function toB64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}
function fromB64(b64: string): Uint8Array {
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

export async function encryptJSON(data: unknown): Promise<{ cipher: string; iv: string } | null> {
  if (!_key) return null;
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(data));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, _key, plaintext);
  return { cipher: toB64(ct), iv: toB64(iv.buffer) };
}

export async function decryptJSON<T = unknown>(cipher: string, iv: string): Promise<T | null> {
  if (!_key) return null;
  try {
    const ivBuf = fromB64(iv) as unknown as BufferSource;
    const ctBuf = fromB64(cipher) as unknown as BufferSource;
    const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: ivBuf }, _key, ctBuf);
    return JSON.parse(new TextDecoder().decode(pt)) as T;
  } catch {
    return null;
  }
}

export function clearKey() {
  _key = null;
  _keyFingerprint = null;
}
